import { useCallback } from 'react'

import { type ChannelId } from '@onr/schema'
import { useQuery } from '@tanstack/react-query'

import { useFirebaseUser } from '@/auth/useFirebaseUser'
import { collections } from '@/schema'
import { userQuery } from '@/state/users/user'

export interface ChannelPreferences {
  muted: boolean
  toggleMute: () => Promise<void>
}

/**
 * For the screens that show alerts from any channel: which channel an alert belongs to is only
 * known per alert, so they need the lookup rather than one channel's preferences.
 */
export const useMutedChannels = (): ((channelId: ChannelId) => boolean) => {
  const uid = useFirebaseUser()?.firebaseUser.uid
  const { data: user } = useQuery(userQuery(uid))
  const channels = user?.data?.channels

  return useCallback((channelId: ChannelId) => channels?.[channelId]?.muted ?? false, [channels])
}

export const useChannelPreferences = (channelId: ChannelId): ChannelPreferences => {
  const uid = useFirebaseUser()?.firebaseUser.uid
  const { data: user } = useQuery(userQuery(uid))
  const muted = user?.data?.channels?.[channelId]?.muted ?? false

  // Dotted-path update rather than a merging set: a set would produce a local snapshot holding only
  // the written fields, which UserZod rejects for its required `timestamp`.
  const toggleMute = useCallback(async () => {
    if (!uid) {
      return
    }
    await collections.users.update(uid, { [`channels.${channelId}.muted`]: !muted })
  }, [uid, channelId, muted])

  return { muted, toggleMute }
}
